import { useCallback, useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { MEDIOS_PAGO_LABEL, calcularArqueo, listCajas } from '../services/cashService';
import { formatDateTime, formatMoney } from '../lib/format';
import { friendlyError } from '../lib/errors';
import LoadingState from '../components/ui/LoadingState';
import ErrorState from '../components/ui/ErrorState';
import EmptyState from '../components/ui/EmptyState';
import Badge from '../components/ui/Badge';

export default function CashDetailPage() {
  const { id } = useParams();
  const { ruc } = useAuth();
  const navigate = useNavigate();
  const [caja, setCaja] = useState(null);
  const [arqueo, setArqueo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const list = await listCajas({ ruc, pageSize: 50 });
      const found = list.find((c) => c.id === id);
      if (!found) throw new Error('Caja no encontrada');
      setCaja(found);
      setArqueo(await calcularArqueo({ ruc, cajaId: id }));
    } catch (e) {
      setError(friendlyError(e));
    } finally {
      setLoading(false);
    }
  }, [ruc, id]);

  useEffect(() => {
    load();
  }, [load]);

  if (loading) return <LoadingState label="Cargando caja…" />;
  if (error) return <ErrorState message={error} onRetry={load} />;
  if (!caja) return <EmptyState title="Caja no encontrada" />;

  return (
    <div className="page">
      <header className="page-header">
        <div>
          <p className="breadcrumb">
            <Link to="/admin/caja">Caja</Link> / {caja.id.slice(0, 8)}
          </p>
          <h1>Caja · {caja.estado}</h1>
          <p className="muted">
            {caja.cajeroName || caja.cajeroId} · {formatDateTime(caja.fechaApertura)}
          </p>
        </div>
        <div className="row-actions">
          <button type="button" className="btn btn-ghost" onClick={() => navigate(-1)}>
            Volver
          </button>
        </div>
      </header>

      <section className="stat-grid compact">
        <div className="stat-card">
          <p className="stat-label">Estado</p>
          <p className="stat-value" style={{ fontSize: '1rem' }}>
            <Badge tone={caja.estado === 'abierta' ? 'ok' : 'muted'}>{caja.estado}</Badge>
          </p>
        </div>
        <div className="stat-card">
          <p className="stat-label">Monto inicial</p>
          <p className="stat-value">{formatMoney(caja.montoInicial)}</p>
        </div>
        <div className="stat-card">
          <p className="stat-label">Monto final</p>
          <p className="stat-value">{formatMoney(caja.montoFinal)}</p>
        </div>
        <div className="stat-card">
          <p className="stat-label">Ventas</p>
          <p className="stat-value">{formatMoney(caja.totalVentas ?? arqueo?.totalVentas ?? 0)}</p>
        </div>
        <div className="stat-card">
          <p className="stat-label">Efectivo</p>
          <p className="stat-value">{formatMoney(caja.ventasEfectivo ?? arqueo?.ventasEfectivo ?? 0)}</p>
        </div>
        {caja.sobrante != null ? (
          <div className="stat-card">
            <p className="stat-label">Sobrante / faltante</p>
            <p className="stat-value">{formatMoney(caja.sobrante)}</p>
          </div>
        ) : null}
      </section>

      <section className="panel">
        <header className="panel-header">
          <h2>Desglose</h2>
        </header>
        {(() => {
          const desglose = caja.desgloseSnapshot || arqueo?.ventasPorMedio || {};
          const entries = Object.entries(desglose);
          if (!entries.length) return <EmptyState title="Sin ventas en esta caja" />;
          return (
            <ul className="simple-list">
              {entries.map(([medio, total]) => (
                <li key={medio}>
                  <span>{MEDIOS_PAGO_LABEL[medio] || medio}</span>
                  <strong>{formatMoney(total)}</strong>
                </li>
              ))}
            </ul>
          );
        })()}
      </section>

      <section className="panel">
        <header className="panel-header">
          <h2>Pedidos vinculados</h2>
          <span className="muted">máx. 20</span>
        </header>
        {(arqueo?.documentos || []).length === 0 ? (
          <EmptyState title="Sin pedidos pagados" />
        ) : (
          <ul className="simple-list">
            {(arqueo?.documentos || []).map((p) => (
              <li key={p.id}>
                <div>
                  <strong>#{p.pedido}</strong>
                  <div className="muted">{p.clienteNombre || p.cliente || '—'}</div>
                </div>
                <div className="row-end">
                  <span>{formatMoney(p.total)}</span>
                  <Badge tone="neutral">{MEDIOS_PAGO_LABEL[p.medioPago] || p.medioPago || '—'}</Badge>
                </div>
              </li>
            ))}
          </ul>
        )}
      </section>
    </div>
  );
}
